/**
 * The workbench's `moneyFormat.ts` seam for the views ported from it (`../components/timeline/`):
 * their DOM-free models format a US-dollar cost with `formatCost(usd)`, which reads the money shown
 * now. In the workbench that comes from the desktop's sign-in; here it is the organisation's
 * currency, which `MoneyProvider` (../money.tsx) hands in once it has loaded. A view that draws such
 * a model calls `useMoneyForModels()` so it draws again when the money changes.
 */
import { formatMoney, USD_MONEY, type OrganisationMoney } from '@aioe/shared';
import { useSyncExternalStore } from 'react';

let current: OrganisationMoney = USD_MONEY;
const listeners = new Set<() => void>();

/** A US-dollar cost in the money shown: `A$12.34`, `US$0.0040`, `US$0.00`. */
export function formatCost(usd: number, money: OrganisationMoney = current, opts: { digits?: number; compact?: boolean } = {}): string {
  return formatMoney(usd, money, opts);
}

/** The organisation's money, from MoneyProvider (called in an effect). */
export function setModelMoney(money: OrganisationMoney): void {
  if (JSON.stringify(money) === JSON.stringify(current)) return;
  current = money;
  for (const l of listeners) l();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => current;

/** The money the models format in; the caller draws again when it changes. */
export function useMoneyForModels(): OrganisationMoney {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
